// formatDate / formatDateRange moved to @novemai/i18n-sdk (src/dates.ts) when
// the mobile app needed the same two formatters — one definition, both apps.
// Re-exported here so this module stays the import site for customer-web's 16
// call sites; the tenure helpers below are app-local and stay.
export { formatDate, formatDateRange } from "@novemai/i18n-sdk";

// Whole-month tenure (kıdem) from a date-only start ("2021-03-12") to now,
// computed with UTC getters under the SAME zone pin the shared formatters use so
// the day count never drifts across DST or the viewer's offset. Returns the
// elapsed span as whole {years, months} — the call site formats it through the
// locale-aware {@link formatTenureDuration} so the i18n stays at the component
// (CLAUDE.md: no t() outside React). A start in the future clamps to zero.
export function tenureParts(startIso: string): { years: number; months: number } {
  const start = new Date(startIso);
  const now = new Date();
  let months =
    (now.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - start.getUTCMonth());
  // Not a full month elapsed since the anchor day-of-month — drop the partial.
  if (now.getUTCDate() < start.getUTCDate()) months -= 1;
  if (months < 0) months = 0;
  return { years: Math.floor(months / 12), months: months % 12 };
}

// The narrow i18n `t` surface this helper needs — assignable from react-i18next's
// TFunction (a plain key call + a key+{count} call for the plural interpolation).
// Keeps the lib free of an i18next type import while staying structurally
// compatible with the component's bound `t` (ADR-006: i18n stays at the call site).
export interface TenureTranslate {
  (key: string): string;
  (key: string, opts: { count: number }): string;
}

// Locale-correct tenure label ("3 years 2 months" / "3 yıl 2 ay"). Plural form
// is resolved per Intl.PluralRules(locale) — the host passes the already-bound
// i18n `t` so the duration.* keys resolve at the component (ADR-006: no
// literals; no t() outside React). Under one month elapsed renders the
// `duration.less_than_month` copy; whole years with zero remainder months omit
// the months clause.
//
// A start date that is still ahead of us is NOT "less than a month" of service:
// nobody has served any time yet, and a person hired for next month read as
// "Bir aydan az" (E10) as if they had already begun. `tenureParts` still clamps
// to zero (its callers want a number); only the LABEL tells the two apart.
export function formatTenureDuration(
  locale: string,
  t: TenureTranslate,
  startIso: string,
): string {
  if (new Date(startIso).getTime() > Date.now()) return t("duration.not_started");
  const { years, months } = tenureParts(startIso);
  if (years === 0 && months === 0) return t("duration.less_than_month");

  const pr = new Intl.PluralRules(locale);
  const parts: string[] = [];
  if (years > 0) {
    parts.push(t(`duration.year.${pr.select(years)}`, { count: years }));
  }
  if (months > 0) {
    parts.push(t(`duration.month.${pr.select(months)}`, { count: months }));
  }
  return parts.join(" ");
}
